"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var zlib_1 = require("@stardazed/zlib");
/**
 * Class to apply and create BPS's.
 *
 * @see https://www.romhacking.net/documents/746/
 */
var BPS = /** @class */ (function () {
    function BPS() {
        this.sourceSize = 0;
        this.targetSize = 0;
        this.metaDataString = "";
        this.meta = {};
        this.actionsOffset = 0;
        this.sourceFile = null;
        this.sourceChecksum = 0;
        this.targetFile = null;
        this.targetChecksum = 0;
        this.patchSourceChecksum = 0;
        this.patchTargetChecksum = 0;
        this.patchChecksum = 0;
        this.patchFile = null;
    }
    /**
     * Set the patch file to be used.
     *
     * @param file BPS formatted file.
     */
    BPS.prototype.setPatch = function (file) {
        this.patchFile = new Uint8Array(file);
        // Check BPS1 at beginning of patch file
        var checkHeader = new Uint32Array(file.slice(0, 4))[0];
        if (checkHeader !== 827543618) {
            throw new Error("Not a valid patch file");
        }
        var seek = 4; // skip BPS1
        var decodedSourceSize = this.decodeBPS(this.patchFile, seek);
        this.sourceSize = decodedSourceSize.number;
        seek += decodedSourceSize.length;
        var decodedTargetSize = this.decodeBPS(this.patchFile, seek);
        this.targetSize = decodedTargetSize.number;
        seek += decodedTargetSize.length;
        var decodedMetaDataLength = this.decodeBPS(this.patchFile, seek);
        seek += decodedMetaDataLength.length;
        if (decodedMetaDataLength.number) {
            var metaArray = this.patchFile.slice(seek, seek + decodedMetaDataLength.number);
            for (var i = 0; i < metaArray.byteLength; ++i) {
                this.metaDataString += String.fromCharCode(metaArray[i]);
            }
            this.meta = JSON.parse(this.metaDataString);
            seek += decodedMetaDataLength.number;
        }
        this.actionsOffset = seek;
        var buf32 = new Int32Array(file.slice(file.byteLength - 12));
        this.patchSourceChecksum = buf32[0];
        this.patchTargetChecksum = buf32[1];
        this.patchChecksum = buf32[2];
        if (this.patchChecksum !==
            zlib_1.crc32(this.patchFile.slice(0, this.patchFile.byteLength - 4))) {
            throw new Error("Patch checksum incorrect");
        }
        return this;
    };
    BPS.prototype.setSource = function (file) {
        this.sourceFile = new Uint8Array(file);
        this.sourceChecksum = zlib_1.crc32(file);
        return this;
    };
    BPS.prototype.setTarget = function (file) {
        this.targetFile = new Uint8Array(file);
        this.targetChecksum = zlib_1.crc32(file);
        return this;
    };
    /**
     * Apply the currently loaded patch to the currently loaded file and return the patched array buffer.
     */
    BPS.prototype.applyPatch = function () {
        if (this.patchFile === null) {
            throw new Error("Patch not set");
        }
        if (this.sourceFile === null) {
            throw new Error("Source not set");
        }
        if (this.patchSourceChecksum !== this.sourceChecksum) {
            throw new Error("Source checksum incorrect");
        }
        var newFileSize = 0;
        var seek = this.actionsOffset;
        // determine target filesize
        while (seek < this.patchFile.byteLength - 12) {
            var data = this.decodeBPS(this.patchFile, seek);
            var action = {
                type: data.number & 3,
                length: (data.number >> 2) + 1
            };
            seek += data.length;
            newFileSize += action.length;
            switch (action.type) {
                case BPS.ACTION_TARGET_READ:
                    seek += action.length;
                    break;
                case BPS.ACTION_SOURCE_COPY:
                case BPS.ACTION_TARGET_COPY:
                    seek += this.decodeBPS(this.patchFile, seek).length;
                    break;
            }
        }
        var tempFile = new ArrayBuffer(newFileSize);
        var tempFileView = new Uint8Array(tempFile);
        // patch
        var outputOffset = 0;
        var sourceRelativeOffset = 0;
        var targetRelativeOffset = 0;
        seek = this.actionsOffset;
        while (seek < this.patchFile.byteLength - 12) {
            var data = this.decodeBPS(this.patchFile, seek);
            var data2 = void 0;
            var action = {
                type: data.number & 3,
                length: (data.number >> 2) + 1
            };
            seek += data.length;
            switch (action.type) {
                case BPS.ACTION_SOURCE_READ:
                    for (var i = 0; i < action.length; ++i) {
                        tempFileView[outputOffset + i] = this.sourceFile[outputOffset + i];
                    }
                    outputOffset += action.length;
                    break;
                case BPS.ACTION_TARGET_READ:
                    for (var i = 0; i < action.length; ++i) {
                        tempFileView[outputOffset + i] = this.patchFile[seek + i];
                    }
                    outputOffset += action.length;
                    seek += action.length;
                    break;
                case BPS.ACTION_SOURCE_COPY:
                    data2 = this.decodeBPS(this.patchFile, seek);
                    seek += data2.length;
                    sourceRelativeOffset +=
                        (data2.number & 1 ? -1 : 1) * (data2.number >> 1);
                    while (action.length--) {
                        tempFileView[outputOffset] = this.sourceFile[sourceRelativeOffset];
                        outputOffset++;
                        sourceRelativeOffset++;
                    }
                    break;
                case BPS.ACTION_TARGET_COPY:
                    data2 = this.decodeBPS(this.patchFile, seek);
                    seek += data2.length;
                    targetRelativeOffset +=
                        (data2.number & 1 ? -1 : 1) * (data2.number >> 1);
                    while (action.length--) {
                        tempFileView[outputOffset] = tempFileView[targetRelativeOffset];
                        outputOffset++;
                        targetRelativeOffset++;
                    }
                    break;
            }
        }
        this.setTarget(tempFile);
        if (this.patchTargetChecksum !== this.targetChecksum) {
            throw new Error("Target checksum incorrect");
        }
        return tempFile;
    };
    /**
     * Create a patch from the source and target binaries and return it as an
     * array buffer.
     */
    BPS.prototype.createPatch = function () {
        throw new Error("Not Currently Implemented");
    };
    /**
     * Convert BPS number format into number.
     *
     * @todo this is inherrently dangerous with while(true)
     *
     * @param dataBytes
     * @param i
     */
    BPS.prototype.decodeBPS = function (dataBytes, i) {
        var number = 0;
        var shift = 1;
        var len = 0;
        while (true) {
            var x = dataBytes[i];
            i++;
            len++;
            number += (x & 0x7f) * shift;
            if (x & 0x80) {
                break;
            }
            shift <<= 7;
            number += shift;
        }
        return {
            number: number,
            length: len
        };
    };
    /**
     * Convert number into BPS number format.
     *
     * @todo this is inherrently dangerous with while(true)
     *
     * @param toEncode
     */
    BPS.prototype.encodeBPS = function (toEncode) {
        var array = [];
        while (true) {
            var x = toEncode & 0x7f;
            toEncode >>= 7;
            if (toEncode === 0) {
                array.push(0x80 | x);
                break;
            }
            array.push(x);
            toEncode--;
        }
        return Uint8Array.from(array);
    };
    BPS.ACTION_SOURCE_READ = 0;
    BPS.ACTION_TARGET_READ = 1;
    BPS.ACTION_SOURCE_COPY = 2;
    BPS.ACTION_TARGET_COPY = 3;
    return BPS;
}());
exports.default = BPS;
